// 方舟一号工具函数
import type { ColorMode, ColorScheme } from '@/types/arkOne'

/**
 * 格式化百分比，添加 +/- 前缀
 * @param value - 数值
 * @param decimals - 小数位数，默认2位
 * @returns 格式化后的字符串，如 "+12.34%" 或 "-5.67%"
 */
export function formatPercentage(value: number, decimals: number = 2): string {
  if (value === 0) return `${value.toFixed(decimals)}%`
  const sign = value >= 0 ? '+' : ''
  return `${sign}${value.toFixed(decimals)}%`
}

/**
 * 格式化价格
 * @param value - 价格数值
 * @param decimals - 小数位数，默认2位
 * @returns 格式化后的价格字符串
 */
export function formatPrice(value: number, decimals: number = 2): string {
  return value.toFixed(decimals)
}

/**
 * 格式化日期
 * @param date - 日期字符串 YYYY-MM-DD
 * @param format - 目标格式 'YYYY-MM-DD' | 'YY/MM/DD'
 * @returns 格式化后的日期字符串
 */
export function formatDate(date: string, format: 'YYYY-MM-DD' | 'YY/MM/DD'): string {
  if (!date) return ''
  
  const parts = date.split('-')
  if (parts.length !== 3) return date
  
  const [year, month, day] = parts
  
  if (format === 'YY/MM/DD') {
    return `${year.slice(2)}/${month}/${day}`
  }
  
  return date
}

/**
 * 获取颜色方案
 * @param mode - 颜色模式
 * @returns 颜色方案对象
 */
export function getColorScheme(mode: ColorMode): ColorScheme {
  if (mode === 'red-rise-green-fall') {
    return {
      riseColor: '#FF0000',
      fallColor: '#00FF00'
    }
  } else {
    return {
      riseColor: '#00FF00',
      fallColor: '#FF0000'
    }
  }
}

/**
 * 根据数值和颜色模式获取颜色
 * @param value - 数值
 * @param mode - 颜色模式
 * @returns 颜色值
 */
export function getValueColor(value: number, mode: ColorMode): string {
  if (value === 0) return 'inherit'
  const scheme = getColorScheme(mode)
  return value > 0 ? scheme.riseColor : scheme.fallColor
}

/**
 * 获取方向颜色（买入/卖出）
 * @param direction - 方向 '买入' | '卖出'
 * @param mode - 颜色模式
 * @returns 颜色值
 */
export function getDirectionColor(direction: '买入' | '卖出', mode: ColorMode): string {
  const scheme = getColorScheme(mode)
  return direction === '买入' ? scheme.riseColor : scheme.fallColor
}

/**
 * 计算年化收益率
 * @param currentTotal - 当前总金额
 * @param yearStartTotal - 年初总金额
 * @returns 年化收益率（百分比）
 */
export function calculateAnnualizedReturn(currentTotal: number, yearStartTotal: number): number {
  if (yearStartTotal <= 0) return 0
  return ((currentTotal - yearStartTotal) / yearStartTotal) * 100
}

/**
 * 计算总收益率
 * @param currentTotal - 当前总金额
 * @param initialCapital - 初始资金
 * @returns 总收益率（百分比）
 */
export function calculateTotalReturn(currentTotal: number, initialCapital: number): number {
  if (initialCapital <= 0) return 0
  return ((currentTotal - initialCapital) / initialCapital) * 100
}

/**
 * 计算股息率
 * @param totalDividends - 总分红金额
 * @param initialCapital - 初始资金
 * @returns 股息率（百分比）
 */
export function calculateDividendYield(totalDividends: number, initialCapital: number): number {
  if (initialCapital <= 0) return 0
  return (totalDividends / initialCapital) * 100
}

/**
 * 计算资金占比百分比
 * @param amount - 金额
 * @param totalCapital - 总资金
 * @returns 占比（百分比）
 */
export function calculatePercentage(amount: number, totalCapital: number): number {
  if (totalCapital <= 0) return 0
  return (amount / totalCapital) * 100
}

/**
 * 计算持仓比例
 * @param currentPrice - 当前价格
 * @param lots - 手数
 * @param totalCapital - 总资金
 * @returns 持仓比例（百分比）
 */
export function calculateHoldingPercentage(
  currentPrice: number,
  lots: number,
  totalCapital: number
): number {
  if (totalCapital <= 0) return 0
  return ((currentPrice * lots) / totalCapital) * 100
}

/**
 * 计算持仓成本
 * @param purchases - 购买记录数组 [{price: number, lots: number}]
 * @returns 平均成本价
 */
export function calculatePositionCost(
  purchases: Array<{ price: number; lots: number }>
): number {
  if (!purchases || purchases.length === 0) return 0
  
  let totalAmount = 0
  let totalLots = 0
  
  purchases.forEach(purchase => {
    totalAmount += purchase.price * purchase.lots
    totalLots += purchase.lots
  })
  
  if (totalLots === 0) return 0
  return totalAmount / totalLots
}

/**
 * 计算年化股息率
 * @param dividendPerShare - 365天每股分红金额
 * @param currentPrice - 当前股价
 * @returns 年化股息率（百分比）
 */
export function calculateAnnualizedDividendYield(
  dividendPerShare: number,
  currentPrice: number
): number {
  if (currentPrice <= 0) return 0
  return (dividendPerShare / currentPrice) * 100
}

/**
 * 计算仓位变化
 * @param prePercentage - 调仓前持仓比例
 * @param postPercentage - 调仓后持仓比例
 * @returns 仓位变化（百分比）
 */
export function calculatePositionChange(
  prePercentage: number,
  postPercentage: number
): number {
  return postPercentage - prePercentage
}

/**
 * 截断文本到指定长度
 * @param text - 原始文本
 * @param maxLength - 最大长度
 * @param ellipsis - 是否添加省略号
 * @returns 截断后的文本
 */
export function truncateText(text: string, maxLength: number, ellipsis: boolean = true): string {
  if (!text || text.length <= maxLength) return text
  return ellipsis ? text.slice(0, maxLength) + '...' : text.slice(0, maxLength)
}

/**
 * 获取数组的前N项
 * @param array - 原始数组
 * @param count - 数量
 * @returns 前N项数组
 */
export function takeFirst<T>(array: T[], count: number): T[] {
  return array.slice(0, count)
}

/**
 * 格式化派息信息
 * @param date - 派息日期 YYYY-MM-DD
 * @param sharesPerDividend - 每N股
 * @param amountPerShare - 派息金额
 * @param currency - 币种
 * @returns 格式化后的派息信息字符串
 */
export function formatDividendInfo(
  date: string,
  sharesPerDividend: number,
  amountPerShare: number,
  currency: string
): string {
  return `${date}，每${sharesPerDividend}股派息${amountPerShare}${currency}`
}

/**
 * 获取图表X轴的3个日期标签（起始、中间、结束）
 * @param startDate - 开始日期
 * @param endDate - 结束日期
 * @returns 3个日期标签数组
 */
export function getChartXAxisLabels(startDate: string, endDate: string): string[] {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const middle = new Date((start.getTime() + end.getTime()) / 2)
  
  return [
    formatDate(startDate, 'YYYY-MM-DD'),
    formatDate(middle.toISOString().split('T')[0], 'YYYY-MM-DD'),
    formatDate(endDate, 'YYYY-MM-DD')
  ]
}

/**
 * 根据时间范围过滤数据
 * @param data - 数据数组（包含date字段）
 * @param timeRange - 时间范围
 * @returns 过滤后的数据数组
 */
export function filterDataByTimeRange<T extends { date: string }>(
  data: T[],
  timeRange: '1M' | '3M' | '6M' | '1Y' | 'ALL'
): T[] {
  if (timeRange === 'ALL' || !data || data.length === 0) return data
  
  const parseDate = (s: string): Date | null => {
    if (!s) return null
    // 兼容 'YYYY-MM-DD'、'YYYY/MM/DD'、'YYYY-MM-DD HH:mm:ss'
    const normalized = s.replace('T', ' ').replace(/-/g, '/')
    const d = new Date(normalized)
    return isNaN(d.getTime()) ? null : d
  }

  // 用最后一个数据点作为“结束时间”，避免因本地时间/数据滞后导致全被过滤掉
  const end = parseDate(data[data.length - 1].date) || new Date()
  let startDate: Date
  
  switch (timeRange) {
    case '1M':
      startDate = new Date(end.getFullYear(), end.getMonth() - 1, end.getDate())
      break
    case '3M':
      startDate = new Date(end.getFullYear(), end.getMonth() - 3, end.getDate())
      break
    case '6M':
      startDate = new Date(end.getFullYear(), end.getMonth() - 6, end.getDate())
      break
    case '1Y':
      startDate = new Date(end.getFullYear() - 1, end.getMonth(), end.getDate())
      break
    default:
      return data
  }
  
  return data.filter(item => {
    const d = parseDate(item.date)
    return d ? d >= startDate : false
  })
}
